import React from 'react';
import { Container, Card, Alert, Button, Spinner } from 'react-bootstrap';
import { FaSync } from 'react-icons/fa';

// Import sub-components
import { MessageFilters } from './index';
import { MessageTable } from './index';
import { RawMessageModal } from './index';
import { MissingIdsWarning } from './index';

// Import custom hook and utilities
import { useGuestyMessages } from './useGuestyMessages';
import { formatDateTime, formatISOToLocalDateTime, customStyles } from './utils';

/**
 * Main component for displaying Guesty webhook message history
 */
export default function GuestyMessageHistory() {
    // Use custom hook to manage all state and operations
    const {
        // State
        filteredMessages,
        isLoading,
        error,
        searchText,
        selectedCustomerId,
        customerSearchText,
        filteredCustomers,
        showCustomerDropdown,
        customerMap,
        addressSearchText,
        uniqueAddresses,
        filteredAddresses,
        showAddressDropdown,
        reservationIdSearchText,
        uniqueReservationIds,
        filteredReservationIds,
        showReservationIdDropdown,
        showRawMessage,
        rawMessage,
        rawMessageLoading,
        rawMessageError,
        isToggling,
        togglingId,
        missingIds,
        showMissingIdsWarning,
        selectedStatus,
        statusTypes,
        statusCounts,
        messages,
        dateHighlightMap,
        
        // Setters
        setFilteredMessages,
        setAddressSearchText,
        setReservationIdSearchText,
        setShowCustomerDropdown,
        setShowAddressDropdown,
        setShowReservationIdDropdown,
        setShowRawMessage,
        setShowMissingIdsWarning,
        
        // Handlers
        handleViewRawMessage,
        handleCustomerFilterChange,
        handleCustomerSearchChange,
        handleStatusFilter,
        handleSearchChange,
        handleToggleStatus,
        refreshMessages
    } = useGuestyMessages();
    
    return (
        <Container fluid className="py-4">
            <Card className="shadow-sm">
                <Card.Header as="h5" className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <div>
                        <img 
                            src="/guesty_log.jpg" 
                            alt="Guesty" 
                            style={{ 
                                width: '24px', 
                                height: '24px', 
                                objectFit: 'contain',
                                marginRight: '10px',
                                background: 'white',
                                borderRadius: '4px',
                                padding: '2px'
                            }} 
                        /> 
                        Guesty Message History
                    </div>
                    <Button 
                        variant="light" 
                        size="sm" 
                        onClick={refreshMessages}
                        disabled={isLoading}
                    >
                        <FaSync className={isLoading ? "me-2 fa-spin" : "me-2"} /> Refresh
                    </Button>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" className="mb-4">
                            {error}
                        </Alert>
                    )}
                    
                    {/* Missing IDs Warning */}
                    <MissingIdsWarning 
                        missingIds={missingIds} 
                        show={showMissingIdsWarning} 
                        onClose={() => setShowMissingIdsWarning(false)} 
                    />
                    
                    {/* Filters */}
                    <MessageFilters 
                        // Status filter props
                        selectedStatus={selectedStatus}
                        statusTypes={statusTypes}
                        statusCounts={statusCounts}
                        handleStatusFilter={handleStatusFilter}
                        messagesCount={messages.length}
                        
                        // Customer filter props
                        customerSearchText={customerSearchText}
                        handleCustomerSearchChange={handleCustomerSearchChange}
                        showCustomerDropdown={showCustomerDropdown}
                        setShowCustomerDropdown={setShowCustomerDropdown}
                        filteredCustomers={filteredCustomers}
                        selectedCustomerId={selectedCustomerId}
                        handleCustomerFilterChange={handleCustomerFilterChange}
                        
                        // Address filter props
                        addressSearchText={addressSearchText}
                        setAddressSearchText={setAddressSearchText}
                        showAddressDropdown={showAddressDropdown}
                        setShowAddressDropdown={setShowAddressDropdown}
                        filteredAddresses={filteredAddresses}
                        messages={messages}
                        setFilteredMessages={setFilteredMessages}
                        
                        // Reservation ID filter props
                        reservationIdSearchText={reservationIdSearchText}
                        setReservationIdSearchText={setReservationIdSearchText}
                        showReservationIdDropdown={showReservationIdDropdown}
                        setShowReservationIdDropdown={setShowReservationIdDropdown}
                        filteredReservationIds={filteredReservationIds}
                        
                        // Search props
                        searchText={searchText}
                        handleSearchChange={handleSearchChange}
                    />
                    
                    {/* Message Table */}
                    {isLoading && !filteredMessages.length ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading messages...</p>
                        </div>
                    ) : (
                        <MessageTable 
                            filteredMessages={filteredMessages}
                            customStyles={customStyles}
                            isLoading={isLoading}
                            handleViewRawMessage={handleViewRawMessage}
                            formatDateTime={formatDateTime}
                            customerMap={customerMap}
                            handleToggleStatus={handleToggleStatus}
                            isToggling={isToggling}
                            togglingId={togglingId}
                            dateHighlightMap={dateHighlightMap}
                        />
                    )}
                </Card.Body>
            </Card>
            
            {/* Raw Message Modal */}
            <RawMessageModal 
                show={showRawMessage}
                onHide={() => setShowRawMessage(false)}
                rawMessage={rawMessage}
                rawMessageLoading={rawMessageLoading}
                rawMessageError={rawMessageError}
                formatISOToLocalDateTime={formatISOToLocalDateTime}
            />
        </Container>
    );
}
